'use client'

import { motion } from 'framer-motion'
import { Space_Grotesk, Outfit } from 'next/font/google'
import { BackgroundBeams } from '../../_components/_ui/background-beams'
import { FaCheck, FaTimes, FaThumbsUp, FaDiscord } from 'react-icons/fa'
import Image from 'next/image'
import { useState } from 'react'
import { AnimatePresence } from 'framer-motion'

// Import SVG icons as paths instead
const TurboIcon = '/images/turbo.svg'
const FocusIcon = '/images/focus.svg'
const SummitIcon = '/images/summit.svg'

const spaceGrotesk = Space_Grotesk({
    subsets: ['latin'],
    variable: '--font-space-grotesk',
    display: 'swap',
})

const outfit = Outfit({
    subsets: ['latin'],
    variable: '--font-outfit',
    display: 'swap',
})

// Custom styles for the SVG icons
const iconStyles = `
  .filter-white {
    filter: brightness(0) invert(1);
  }
  .filter-indigo {
    filter: invert(33%) sepia(93%) saturate(1352%) hue-rotate(230deg) brightness(99%) contrast(96%);
  }
`

const PricingTier = ({
    name,
    price,
    description,
    features,
    isPopular,
    isPlus,
    delay,
    icon,
}) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay }}
            className={`relative bg-neutral-900/50 backdrop-blur-sm rounded-2xl p-6 border ${
                isPopular ? 'border-indigo-500' : 'border-neutral-800'
            } hover:border-neutral-700 transition-all duration-300`}
        >
            {isPopular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-indigo-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                        Most Popular
                    </span>
                </div>
            )}
            <div className="text-center">
                {icon && (
                    <div className="flex justify-center mb-3">
                        <img
                            src={icon}
                            alt={`${name} plan icon`}
                            width={48}
                            height={48}
                            className={`${
                                isPopular || isPlus
                                    ? 'text-indigo-500 filter-indigo'
                                    : 'text-white filter-white'
                            }`}
                        />
                    </div>
                )}
                <h3 className="text-xl font-bold text-white mb-1">{name}</h3>
                <div className="text-3xl font-bold text-white mb-2">
                    ${price}
                    <span className="text-base text-gray-400">/month</span>
                </div>
                <p className="text-sm text-gray-400 mb-6">{description}</p>
            </div>
            <ul className="space-y-3 mb-6 text-sm">
                {features.map((feature, index) => (
                    <li key={index} className="flex items-center text-gray-300">
                        {feature.icon === 'check' ? (
                            <FaCheck className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                        ) : feature.icon === 'thumbs-up' ? (
                            <FaThumbsUp className="h-4 w-4 text-white-500 mr-2 flex-shrink-0" />
                        ) : (
                            <FaTimes className="h-4 w-4 text-red-500 mr-2 flex-shrink-0" />
                        )}
                        <span className="line-clamp-2">{feature.text}</span>
                    </li>
                ))}
            </ul>
            <a
                href="/sign-up"
                className={`block w-full text-center py-2 px-4 rounded-md font-medium transition-colors text-sm ${
                    isPopular
                        ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                        : 'bg-neutral-800 text-white hover:bg-neutral-700'
                }`}
            >
                Get Started
            </a>
        </motion.div>
    )
}

const PageVisitorsInfo = () => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16"
        >
            <div className="bg-blue-900/50 backdrop-blur-sm rounded-2xl p-8 border border-blue-700">
                <h2 className="text-2xl font-bold text-white mb-4 text-center">
                    About Page Visit Limits
                </h2>
                <p className="text-gray-200 mb-4">
                    As we transition to becoming a web hosting platform for your focused content, 
                    we want to be transparent about how traffic affects our costs. Higher page 
                    visits result in increased server demands and costs for us.
                </p>
                <p className="text-gray-200 mb-4">
                    Currently, our visit limits are set at preliminary levels while we gather 
                    data on actual usage patterns. During this alpha phase, even Basic users 
                    receive the same allocation as paid tiers to help us calculate hosting costs.
                </p>
                <p className="text-gray-200 mb-4">
                    <strong>Please note:</strong> These limits will change in the future as we 
                    refine our understanding of hosting costs. Our goal is to continue providing 
                    Basic users with access while ensuring the sustainability of our platform.
                </p>
                <p className="text-gray-200">
                    We'll always communicate changes well in advance and work to find the right 
                    balance between affordability and sustainability.
                </p>
            </div>
        </motion.div>
    )
}

const Pricing = () => {
    const tiers = [
        {
            name: 'Basic',
            price: '0',
            description: 'For casual creators',
            features: [
                { text: '0 additional pages', icon: 'x' },
                { text: '7 blocks per page', icon: 'check' },
                { text: 'No image/file hosting', icon: 'check' },
                { text: 'No premium blocks', icon: 'x' },
                { text: 'No custom domains', icon: 'x' },
                { text: 'No analytics', icon: 'x' },
                { text: 'Very low ad block profit return', icon: 'x' },
                { text: 'No Custom Styling', icon: 'x' },
                { text: '1,000 page visitors per month', icon: 'check' },
            ],
            icon: FocusIcon,
        },
        {
            name: 'Turbo',
            price: '6.99',
            description: 'For growing creators',
            isPopular: true,
            features: [
                { text: '5 additional pages', icon: 'check' },
                { text: '15 blocks per page', icon: 'check' },
                { text: 'No image/file hosting', icon: 'check' },
                { text: 'No premium blocks', icon: 'x' },
                { text: 'No custom domains', icon: 'x' },
                { text: 'Basic analytics', icon: 'thumbs-up' },
                { text: '50% ad block profit return', icon: 'thumbs-up' },
                { text: 'No Custom Styling', icon: 'x' },
                { text: '10,000 page visitors per month', icon: 'check' },
            ],
            icon: TurboIcon,
        },
        {
            name: 'Summit',
            price: '16.99',
            description: 'For professional creators',
            features: [
                { text: 'Unlimited pages', icon: 'check' },
                { text: 'Unlimited blocks per page', icon: 'check' },
                { text: 'Full resolution image and file hosting', icon: 'check' },
                { text: 'Full premium block access', icon: 'check' },
                { text: 'Custom domains', icon: 'check' },
                { text: 'Advanced analytics', icon: 'check' },
                { text: '100% ad block profit return', icon: 'check' },
                { text: 'Custom Styling', icon: 'check' },
                { text: '100,000 page visitors per month', icon: 'check' },
            ],
            icon: SummitIcon,
        },
        {
            name: 'Summit+',
            price: '39.99+',
            isPlus: true,
            description:
                'a personal nerd to build in custom features to show off whatever your focus is',
            features: [
                { text: 'Unlimited pages', icon: 'check' },
                { text: 'Unlimited blocks per page', icon: 'check' },
                { text: 'Full File Hosting Suite', icon: 'check' },
                { text: 'Custom Blocks', icon: 'check' },
                { text: 'Custom domains', icon: 'check' },
                { text: 'Advanced analytics', icon: 'check' },
                { text: '100% ad block profit return', icon: 'check' },
                { text: 'Priority In House Support', icon: 'check' },
                { text: 'Unlimited page visitors', icon: 'check' },
            ],
            icon: SummitIcon,
        },
    ]

    return (
        <div
            className={`min-h-screen w-full relative ${spaceGrotesk.variable} ${outfit.variable}`}
        >
            <style jsx global>
                {iconStyles}
            </style>
            <BackgroundBeams />
            <div className="relative z-10">
                {/* Hero Section */}
                <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto text-center">
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className={`text-4xl sm:text-5xl font-bold text-white leading-tight mb-6 ${spaceGrotesk.className}`}
                        >
                            (proposed) Simple, Transparent Pricing
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="text-xl text-gray-300"
                        >
                            Choose the plan that's right for you. We believe in
                            transparent pricing - pay only for the features you
                            need after exploring our platform. No hidden fees or
                            auto-renewing trials you might forget about.
                        </motion.p>
                    </div>
                </div>

                {/* Pricing Grid */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {tiers.map((tier, index) => (
                            <PricingTier
                                key={tier.name}
                                {...tier}
                                delay={index * 0.1}
                            />
                        ))}
                    </div>
                </div>

                {/* Page Visitors Info */}
                <PageVisitorsInfo />

                {/* Early Adopter/Investor Section */}
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="bg-gradient-to-r from-indigo-900/70 to-purple-900/70 backdrop-blur-sm rounded-2xl p-8 border border-indigo-700"
                    >
                        <h2 className="text-2xl font-bold text-white mb-4 text-center">
                            Lifetime Packages for Early Adopters & Investors
                        </h2>
                        <p className="text-gray-200 text-center mb-6">
                            Join us on the ground floor with exclusive lifetime access packages. 
                            Be part of our journey and secure premium features forever with a one-time investment.
                        </p>
                        <div className="flex flex-col items-center">
                            <ul className="text-gray-300 mb-6 space-y-2">
                                <li className="flex items-center">
                                    <FaCheck className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                                    <span>Lifetime access to premium features</span>
                                </li>
                                <li className="flex items-center">
                                    <FaCheck className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                                    <span>Early access to new features</span>
                                </li>
                                <li className="flex items-center">
                                    <FaCheck className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                                    <span>Exclusive investor community access</span>
                                </li>
                                <li className="flex items-center">
                                    <FaCheck className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                                    <span>Direct input on product roadmap</span>
                                </li>
                            </ul>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <a
                                    href="mailto:adopt@autofoc.us"
                                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                                >
                                    Contact Us at adopt@autofoc.us
                                </a>
                                <a
                                    href="https://discord.gg/PamuMGvPvj"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-[#5865F2] hover:bg-[#4752c4] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5865F2] transition-colors"
                                >
                                    <FaDiscord className="mr-2 h-5 w-5" />
                                    Join our Discord
                                </a>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* FAQ Section */}
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        className="bg-neutral-900/50 backdrop-blur-sm rounded-2xl p-8 border border-neutral-800"
                    >
                        <h2 className="text-2xl font-bold text-white mb-8 text-center">
                            Frequently Asked Plan Questions
                        </h2>
                        <div className="space-y-6">
                            {[
                                {
                                    question: 'Can I change plans later?',
                                    answer: 'Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle. Features on the new plan will be added (or removed) from your account after the next billing period ends.',
                                },
                                {
                                    question:
                                        'What payment methods do you accept?',
                                    answer: 'We accept all major credit cards, PayPal, and bank transfers for Enterprise plans.',
                                },
                                {
                                    question: 'Do you offer refunds?', 
                                    answer: "Refunds are handled on a case-by-case basis. If you're not satisfied with the service, please contact us and we'll do our best to help.",
                                },
                                {
                                    question: 'What exactly is a "personal nerd" in the Summit+ plan?',
                                    answer: "Imagine having your own tech-savvy sidekick who actually answers your calls! Your personal nerd is like having the IT equivalent of a butler - but one who's obsessed with making your Autofocus site amazing instead of serving tea. Restaurant owners can have custom menu blocks that update in real-time, businesses can showcase products with specialized galleries, and creators can have completely unique features built just for them. It's like having someone who speaks fluent 'computer' translate your wildest website dreams into reality. They'll fine-tune Autofocus to your specific needs faster than you can say 'have you tried turning it off and on again?' - except you won't need to, because they'll make sure everything works perfectly. We can't do everything, but we can do a lot.",
                                },
                                {
                                    question: 'What are the lifetime packages for early adopters?',
                                    answer: "Our lifetime packages are designed for early supporters who want to invest in Autofocus's future. These packages offer permanent access to premium features with a one-time payment instead of recurring subscriptions. Each package is customized based on your needs and investment level. For detailed information, please contact us at adopt@autofoc.us.",
                                },
                                {
                                    question: 'What happens if I exceed my page visitor limit?',
                                    answer: "During our alpha phase, we're monitoring usage patterns and won't strictly enforce visitor limits. If your site consistently exceeds its tier's limit, we'll reach out to discuss upgrading options. Our goal is to be fair while ensuring our hosting infrastructure remains sustainable. As we evolve, we'll provide clearer policies on overage charges or throttling, always with advance notice.",
                                },
                                {
                                    question: 'Need help choosing?',
                                    answer: "Contact our support team and we'll help you find the perfect plan for your needs.",
                                },
                            ].map((faq, index) => (
                                <Accordion
                                    key={index}
                                    question={faq.question}
                                    answer={faq.answer}
                                />
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    )

    // Accordion component for FAQ items
    function Accordion({ question, answer }) {
        const [isOpen, setIsOpen] = useState(false)

        return (
            <div className="border border-neutral-800 rounded-lg overflow-hidden">
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="flex justify-between items-center w-full p-4 text-left text-white font-medium focus:outline-none"
                >
                    <h3 className="text-lg font-medium text-white">
                        {question}
                    </h3>
                    <svg
                        className={`w-5 h-5 transition-transform ${
                            isOpen ? 'transform rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                </button>
                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                        >
                            <div className="p-4 pt-0 text-gray-400">
                                {answer}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        )
    }
}

export default Pricing
